import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Nav, Spinner } from "react-bootstrap";
import { isAdminRole, isInternal, isProjectStandardRole, isProjectReadOnlyRole, isLenderRole, isTPORole } from "../../../redux/actions/userAction";
import { gridConfig } from "../../shared/constants/AgGrid/GridConfig";
import { AssignReviewerModal } from "../AssignReviewer/AssignReviewerModal";
import { getColumnDefs, columnOrder, autoSizeStrategy, rowSelection } from "./AgGrid/GridOptions";
import LenderDashboard from "../../LenderDashboard/LenderDashboard";
import { REQUEST_STATUSES, isClosedStatus } from "../../shared/constants/RequestStatuses";
import { REQUEST_TYPES } from "../../shared/constants/RequestTypes";

import "bootstrap/dist/css/bootstrap.min.css";
import "./LenderRequestList.css";
import { getAllLenderRequests, isDraftRequest } from "../../shared/services/LenderRequestService";
import { fetchReviewers } from "../../shared/services/LenderRequestService";
import { isProjectRole } from "../../../redux/actions/userAction";

const FILTER_TYPES = {
  OPEN: "open",
  COMPLETED: "completed",
  ASSIGNED_TO_ME: "assigned-to-me",
  ESCALATED: "escalated",
  LENDER: "lender",
  DASHBOARD: "dashboard",
};

const LenderRequestList = (props) => {
  const [allRequests, setAllRequests] = useState();
  const [displayedRows, setDisplayedRows] = useState();
  const [showAssignModal, setShowAssignModal] = useState(false);  
  const [selectedRequest, setSelectedRequest] = useState();
  const [filterType, setFilterType] = useState(
    isInternal() 
      ? FILTER_TYPES.DASHBOARD 
      : FILTER_TYPES.LENDER
  );
  const [loading, setLoading] = useState(true); // Add loading state
  const [modalInitialized, setModalInitialized] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const gridRef = useRef(null);

  // todo: get user data from store instead of session
  const passData = JSON.parse(sessionStorage.getItem("passData"));
  

  const filterActions = {    
    [FILTER_TYPES.OPEN]: {
      rowFilter: req => 
        !req.hasDecision && !req.isDraft,
      columnFilter: col => true
    },
    [FILTER_TYPES.COMPLETED]: {
      rowFilter: req => 
        req.hasDecision && !req.isDraft,
      columnFilter: col => true
    },
    [FILTER_TYPES.ASSIGNED_TO_ME]: {
      rowFilter: req => 
        req.assignedToLdapId?.toLowerCase() === passData?.user?.toLowerCase()
        && !req.isDraft,
      columnFilter: col => true
    },
    [FILTER_TYPES.ESCALATED]: {
      rowFilter: req => req.requestStatus === REQUEST_STATUSES.ESCALATED,
      columnFilter: col => true
    },
    [FILTER_TYPES.LENDER]: {
      rowFilter: () => true,
      columnFilter: col => 
        col.field !== "assignedTo" && 
        col.field !== "subType" &&
        col.field !== "previouslyWorkedBy" &&
        col.field !== "requestStatusDate" &&
        col.headerTooltip !== "Escalation Indicator"
    },
  }

  // First we generate a fresh set of column defs so we can modify it without potential side effects.
  // Then we adjust a few properties according to the user's role.
  // Then we turn that into an array using the preset column order. This array will be our master list
  // of column defs. 
  const allColumns = useMemo(() => {
    const columnLookup = getColumnDefs();

    if (isLenderRole() || isTPORole()) {
      delete columnLookup.assignedTo.minWidth;
      delete columnLookup.assignedTo.cellRenderer;
    }

    return columnOrder.map(fieldName => columnLookup[fieldName]);
  }, []);

  // The default column view is all columns.
  const [columnView, setColumnView] = useState(allColumns);

  useEffect(() => {
    if (!showAssignModal) {
      setRequestList();
    }
  }, [showAssignModal]);


  // Now that we have the master list of columns, filter them and the data according to the selected view.
  useEffect(() => {
    if (allRequests && filterActions[filterType]) {
      const visibleRows = allRequests.filter(filterActions[filterType].rowFilter);
      setDisplayedRows(visibleRows);
      
      const visibleColumns = allColumns.filter(filterActions[filterType].columnFilter);
      setColumnView(visibleColumns);

      // Log Draft requests filtered from internal views
      const draftCount = allRequests.filter(r => r.isDraft).length;
      if (draftCount > 0) console.debug('[SFMLCM016-759] draft_request_filtered', { draftCount });
    }
  }, [allRequests, filterType, allColumns]);

  // Clear grid filters and refresh data when switching tabs
  useEffect(() => {
    if (filterType && filterType !== FILTER_TYPES.DASHBOARD) {
      // Clear AG Grid column filters
      if (gridRef.current?.api) {
        gridRef.current.api.setFilterModel(null);
      }
      // Refresh data from the API
      setRequestList();
    }
  }, [filterType]);

  useEffect(() => {
    if (isInternal())  {
      (async function () {
        try {
          setLoading(true); // Set loading to true before fetching data
          let reviewersList = await fetchReviewers();
          // Sort the requestList alphabetically by a property userName
          reviewersList = reviewersList.sort((a, b) => {
            const nameA = a.userName?.toLowerCase() || ""; 
            const nameB = b.userName?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
          });
          console.log("Reviewers list:", reviewersList);
          setReviewers(reviewersList);

          setModalInitialized(true);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false); // Set loading to false after data is fetched
        }
      })();
    }
  }, []);

  async function setRequestList() {
    try {
      setLoading(true);
      const requestList = await getAllLenderRequests();
      let list = [];
      
      requestList.forEach(req => {
        const statusName = req?.statusName || '';
        const hasDecision = isClosedStatus(statusName);

        list.push({
          requestType: req?.requestTypeName || '',
          projectId: req?.projectId,
          projectName: req?.projectName || '',
          requestStatus: isDraftRequest(req)
            ? REQUEST_STATUSES.DRAFT
            : (isInternal() || isProjectReadOnlyRole()) ? (req?.internalStatus || '') : (req?.externalStatus || ''),
          assignedTo: req?.assignedToName ? req?.assignedToName : (req?.assignedToLdapId || ''),
          previouslyWorkedBy: req?.previouslyWorkedByName || '',
          requestStatusDate: req?.latestStatusChangeDate || req?.requestStatusDate || null,
          slaColor: req?.slaColor || null,
          dateCreated: (statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_RECEIVED || isClosedStatus(statusName))
              ? req.requestUpdatedDate 
              : req.requestCreatedDate,
          lender: req?.lenderName ? req?.lenderName : (req?.lenderId || ''),
          createdBy: req?.requestCreatedByName ? req?.requestCreatedByName : (req?.requestCreatedByLdapId || ''),
          hasDecision: hasDecision,
          requestId: req?.requestId || '',
          requestStatusId: req?.projectRequestStatusId || '',
          projectRequestStatusId: req?.projectRequestStatusId || '',
          assignedToLdapId: req?.assignedToLdapId || '',
          internalStatus: req?.internalStatus || '',
          externalStatus: req?.externalStatus || '',
          statusName: statusName,
          // isDraftRequest: checks externalStatus and statusName case-insensitively.
          // internalStatus is '' for Draft in the DB, so it can't be used.
          isDraft: isDraftRequest(req),
          isEscalated: req?.isEscalated === true || (req?.requestFeedActivityTrackerDTOList || []).some(activity => activity?.isEscalated === true),
          requestFeedActivityTrackerDTOList: req?.requestFeedActivityTrackerDTOList || [],
          // Add fields needed for sub-type column valueGetter
          reasonCodes: req?.reasonCodes || [],
          requestFeedDocumentDTOList: req?.requestFeedDocumentDTOList || [],
          requestSubTypeName: req?.requestSubTypeName || ''
        });
      });

      setAllRequests(list);
      // Filter will be applied by the useEffect that watches allRequests
    } catch (error) {
      console.error("Error fetching lender requests:", error);
    } finally {
      setLoading(false);
    }
  }

  // Listen for when the user clicks the edit icon on the assignedTo field
  useEffect(() => {
    document.addEventListener("grid-edit-start", openAssignModal);
    
    // Listen for assignment completion to refresh the list
    const handleAssignmentComplete = () => {
      setRequestList(); // Refresh the list data
    };
    
    document.addEventListener("assignment-complete", handleAssignmentComplete);
    
    return () => {
      document.removeEventListener("grid-edit-start", openAssignModal);
      document.removeEventListener("assignment-complete", handleAssignmentComplete);
    };
  }, []);

  function onCellClicked(params) {
    // ignore if the user clicked the edit icon
    // todo: use a more robust way to identify control icons
    if (params.column.colId !== "assignedTo" || params.event?.target.nodeName.toLowerCase() === "span") {
      if (
        params.data.requestType === REQUEST_TYPES.LENDER_ADD_PROJECT &&
        (!params.data.hasDecision ||
        params.data.statusName === REQUEST_STATUSES.NO_ACTION_TAKEN ||
        params.data.statusName === REQUEST_STATUSES.REVIEWED) &&
        params.data.statusName !== REQUEST_STATUSES.ACTION_TAKEN
      ) {
        window.location.href = '/lender-project-request/' + params.data.projectId;
      } else {
        window.location.href = '/project/' + params.data.projectId;
      }
    }
  }

  // Opens the "assign reviewer" modal when the user clicks the edit icon
  function openAssignModal(event) {
    setSelectedRequest(event.detail);
    setShowAssignModal(true);
  }

  // todo: test with long reviewer names
  return (
    <>
      <div className="request-list-title">Lender Project Request</div>
      <div className="request-list-content">
        {isInternal() ? (
          <Nav activeKey={filterType} className="request-filter-buttons" onSelect={key => setFilterType(key)}>
            <Nav.Item>
              <Nav.Link eventKey={FILTER_TYPES.DASHBOARD}>Dashboard</Nav.Link>
            </Nav.Item>
            <Nav.Item><Nav.Link eventKey={FILTER_TYPES.OPEN}>Open Requests</Nav.Link></Nav.Item> 
            <Nav.Item><Nav.Link eventKey={FILTER_TYPES.COMPLETED}>Completed Requests</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey={FILTER_TYPES.ESCALATED}>Escalated</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey={FILTER_TYPES.ASSIGNED_TO_ME}>Assigned to Me</Nav.Link></Nav.Item>
          </Nav>
        ) : (<></>)}
        {loading ? (
          <div className="spinner-container">
            <Spinner animation="border" role="status" className="custom-spinner">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            {filterType === FILTER_TYPES.DASHBOARD ? (
              <LenderDashboard />
            ) : displayedRows ? (
              <>
                {displayedRows.length 
                  ? (<div style={{ height: "100%", width: "100%" }}>
                      <div style={{ height: "438px", width: "100%" }}>
                        <AgGridReact 
                          ref={gridRef}
                          {...gridConfig} 
                          paginationAutoPageSize={false}
                          paginationPageSize={20}
                          paginationPageSizeSelector={[20, 50, 100]}
                          rowData={displayedRows} 
                          columnDefs={columnView}
                          rowSelection={rowSelection}
                          autoSizeStrategy={autoSizeStrategy}
                          onCellClicked={onCellClicked}
                          tooltipShowDelay={300}
                          tooltipHideDelay={0}
                          tooltipMouseTrack={true}
                          enableBrowserTooltips={false}
                          getRowStyle={(params) => {
                            if (!isInternal() && !isProjectReadOnlyRole()) return null;
                            const status = params.data?.requestStatus || params.data?.statusName || params.data?.internalStatus;
                            const isEscalated = status === REQUEST_STATUSES.ESCALATED || params.data?.isEscalated === true;
                            if (isEscalated) return { background: '#fff9c4' };
                            return null;
                          }}
                        />
                      </div>
                    </div>
                  ) : (<h5>No new project requests to display</h5>)}
              </>
            ) : (<h4>Loading...</h4>)}
          </>
        )}
      </div>

      <>
        {modalInitialized && (
          <AssignReviewerModal
            selectedRequest={selectedRequest}
            show={showAssignModal}
            setShow={setShowAssignModal}
            reviewers={reviewers}
          />
        )}
      </>
    </>
  );
}

export default LenderRequestList;

